import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { STATIC_PATH } from "../../constants/config";
import {
  Icons,
  PlayerContainer,
  PlayerControls,
  Seek,
  SeekBar,
  PlayTimer,
  VolumeContainer,
  VolumeSlider,
} from "../../animo-player/animo-component";
import {
  playPausePlayer,
  setAnimoFullScreen,
  setGlobalVolume,
  setPlayAll,
  setPlayHeadTime,
  showFeedbackModal,
  startPlay,
} from "../../redux/actions/appUtils";
import { secondsToMinute } from "../timeline/timeline-helper";
import ActionTypes from "../../constants/action-types";
import Slider from "../zoom/zoom-slider";
import { Duration, PlayButton } from "./shorts-components";
import contextActionType from "./context-action-type";
import { useShortsContext } from "./shortsContextApi";
import isIphoneSafari from "../../helper/isIphoneSafari";

const {
  GET_STYLES_OPEN,
  SET_STYLES_OPEN,
  SET_ACTIVE_PLAYER,
  GET_PRESET_MODAL_OPEN,
  GET_REFRAME_LOADING,
} = contextActionType;

const ShortsPlayerControlPanel = ({
  startTime,
  endTime,
  videoStartTime,
  isAireframeShort,
  playbackVersion,
  videoRef,
  isShorts,
  isVideoReady,
}) => {
  const t1Ref = useRef(null);

  const appReducer = useSelector((state) => state.app);
  const isPlayAll = appReducer.get("isPlayAll");
  const t1 = appReducer.get("t1");
  const pausePlayer = appReducer.get("isPausePlayer");
  const animoPlayerId = appReducer.get("animoPlayerId");
  const projectDuration = useSelector((state) => state.projectDetails.get("duration"));
  const duration = endTime ? endTime - startTime : projectDuration;
  const previewStartTime = videoStartTime ?? startTime;
  const isPlayerLoaded = useSelector((state) => state.app.get("isLoaded") || state.app.get("isAnimLoaded"));
  const dispatch = useDispatch();
  const seekBarRef = useRef();
  const playSeekTimer = useRef();
  const currentTime = useRef();

  const [percentage, setPercentage] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [isPausePlayer, setIsPausePlayer] = useState(false);
  const [isShowControl, setIsShowControl] = useState(false);
  const [isIosSafari, setIsIosSafari] = useState(false);
  const [showPlayPauseIcon, setShowPlayPauseIcon] = useState(false);
  const playPauseIconTimer = useRef(null);
  const replayTimerRef = useRef(null);
  const aireframeLoadFallbackTimerRef = useRef(null);
  const isRestartingAtEndRef = useRef(false);
  const prevPresetOpenRef = useRef(false);
  const isManuallyPausedRef = useRef(false);
  // Tracks whether autoplay has fired for the current short so we don't
  // re-trigger it on subsequent re-renders after the player is loaded.
  const hasAutoplayed = useRef(false);
  const prevT1Ref = useRef(null);
  const prevIsAireframeShortRef = useRef(isAireframeShort);
  const lastAnimoPlayerIdRef = useRef(animoPlayerId);
  const { getShortsContext, setShortsContext } = useShortsContext();
  const isPlayPause = getShortsContext(GET_STYLES_OPEN);
  const presetModalOpen = getShortsContext(GET_PRESET_MODAL_OPEN);
  // True while the AI Reframe API call / polling is in progress — play is blocked.
  const isReframeApplying = getShortsContext(GET_REFRAME_LOADING);

  const restartFromBeginning = useCallback(() => {
    if (videoRef?.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = previewStartTime || 0;
      videoRef.current.style.display = "none";
    }
    clearInterval(playSeekTimer.current);
    currentTime.current = secondsToMinute(0);
    setPercentage(0);
    dispatch(setPlayHeadTime({ playhead: startTime }));
    dispatch(playPausePlayer({ playhead: startTime, isPause: false }));
    setShortsContext(SET_ACTIVE_PLAYER, animoPlayerId);
    isRestartingAtEndRef.current = false;
  }, [animoPlayerId, dispatch, previewStartTime, setShortsContext, startTime, videoRef]);

  useEffect(() => {
    clearTimeout(aireframeLoadFallbackTimerRef.current);

    if (
      !isAireframeShort ||
      !isPlayAll ||
      !t1 ||
      isPlayerLoaded ||
      !isVideoReady ||
      isManuallyPausedRef.current
    ) {
      return undefined;
    }

    aireframeLoadFallbackTimerRef.current = setTimeout(() => {
      dispatch(startPlay("player"));
      dispatch(startPlay("workspace"));
    }, 3000);

    return () => {
      clearTimeout(aireframeLoadFallbackTimerRef.current);
    };
  }, [animoPlayerId, dispatch, isAireframeShort, isPlayAll, isPlayerLoaded, isVideoReady, pausePlayer, t1]);

  useEffect(() => {
    // Only initialize if startTime is available
    if (startTime !== undefined && startTime !== null) {
      // Setting playhead to play start of a shorts.
      dispatch(setPlayHeadTime({ playhead: startTime }));
      // Initialize currentTime display with start time
      currentTime.current = secondsToMinute(0);
      setPercentage(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startTime]);

  // Reset autoplay flags whenever the active short changes (animoPlayerId changes).
  // startTime is always 0 for the control panel so it can't be used as the reset signal.
  useEffect(() => {
    hasAutoplayed.current = false;
    isManuallyPausedRef.current = false;
    isRestartingAtEndRef.current = false;
    clearTimeout(replayTimerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [animoPlayerId]);

  useEffect(() => {
    hasAutoplayed.current = false;
    isRestartingAtEndRef.current = false;
    clearTimeout(replayTimerRef.current);
  }, [animoPlayerId, playbackVersion, isAireframeShort]);

  // Set manual pause flag and force pause state in Redux when preset modal is open
  useEffect(() => {
    if (presetModalOpen) {
      isManuallyPausedRef.current = true;
      dispatch(playPausePlayer({ playhead: startTime || 0, isPause: true }));
      dispatch(setPlayHeadTime({ playhead: startTime || 0 }));
      currentTime.current = secondsToMinute(0);
      setPercentage(0);
    }
  }, [presetModalOpen, dispatch, startTime]);

  // Pause player and reset playhead to 0 when AI Reframe gets applied to the current short
  useEffect(() => {
    const prevIsAireframeShort = prevIsAireframeShortRef.current;
    prevIsAireframeShortRef.current = isAireframeShort;

    const lastAnimoPlayerId = lastAnimoPlayerIdRef.current;
    lastAnimoPlayerIdRef.current = animoPlayerId;

    if (lastAnimoPlayerId === animoPlayerId && !prevIsAireframeShort && isAireframeShort) {
      isManuallyPausedRef.current = true;
      dispatch(playPausePlayer({ playhead: startTime || 0, isPause: true }));
      dispatch(setPlayHeadTime({ playhead: startTime || 0 }));
      currentTime.current = secondsToMinute(0);
      setPercentage(0);
    }
  }, [isAireframeShort, animoPlayerId, dispatch, startTime]);

  // Reset hasAutoplayed when t1 transitions from a timeline back to null.
  // This happens when setPlayAll(false) is dispatched during a reframe rebuild,
  // so the subsequent setPlayAll(true) correctly re-triggers autoplay.
  useEffect(() => {
    const prevT1 = prevT1Ref.current;
    prevT1Ref.current = t1;
    if (prevT1 && !t1) {
      hasAutoplayed.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t1]);

  // Autoplay: as soon as the GSAP timeline (t1) is created by SET_PLAY_ALL
  // AND the player has finished loading (percentage/buffering loader is gone),
  // seed the playhead and start playback. t1 goes from null → TimelineMax
  // exactly when setPlayAll(true) is dispatched from shorts-player.jsx.
  // We also wait for isPlayerLoaded (isLoaded || isAnimLoaded) so that
  // subtitle animation doesn't start while the spinner/buffering loader is visible.
  useEffect(() => {
    if (
      t1 &&
      isPlayerLoaded &&
      !presetModalOpen &&
      !hasAutoplayed.current &&
      !isManuallyPausedRef.current &&
      startTime !== undefined &&
      startTime !== null
    ) {
      hasAutoplayed.current = true;
      dispatch(setPlayHeadTime({ playhead: startTime }));
      dispatch(playPausePlayer({ playhead: startTime, isPause: false }));
      setShortsContext(SET_ACTIVE_PLAYER, animoPlayerId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t1, isPlayerLoaded, startTime, animoPlayerId, presetModalOpen]);

  useEffect(() => {
    // Show icon when hovering for 2 seconds
    if (isShowControl && isShorts) {
      setShowPlayPauseIcon(true);
      if (playPauseIconTimer.current) {
        clearTimeout(playPauseIconTimer.current);
      }
      // Hide icon after 2 seconds on hover (only if video is playing)
      if (isPlayAll && !isPausePlayer) {
        playPauseIconTimer.current = setTimeout(() => {
          setShowPlayPauseIcon(false);
        }, 2000);
      }
    } else if (!isShowControl) {
      // Hide icon when not hovering
      setShowPlayPauseIcon(false);
      if (playPauseIconTimer.current) {
        clearTimeout(playPauseIconTimer.current);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isShowControl, isPlayAll, isPausePlayer]);


  useEffect(() => {
    // Keep the ref updated with the latest value of t1 for pausing the player on tab visibilitychange.
    t1Ref.current = t1;
  }, [t1]);

  /**
  * Function to pause the player on moving to other tab.
  */
  const handleVisibilityChange = () => {
    if (document.hidden) {
      let playhead = startTime;
      if (t1Ref.current && typeof t1Ref.current.time === 'function') {
        playhead = t1Ref.current.time();
      }
      dispatch(playPausePlayer({ playhead, isPause: true }));
    }
  }

  useEffect(() => {
    setIsIosSafari(isIphoneSafari());
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      clearInterval(playSeekTimer.current);
      clearTimeout(playPauseIconTimer.current);
      clearTimeout(replayTimerRef.current);
      clearTimeout(aireframeLoadFallbackTimerRef.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setIsPausePlayer(pausePlayer);
  }, [pausePlayer]);

  useEffect(() => {
    if (isPlayAll && !isPausePlayer && startTime !== undefined && startTime !== null) {
      playSeekTimer.current = setInterval(() => {
        // use recent props for interval callback
        // Ensure t1 is available before calling time()
        if (t1 && typeof t1.time === 'function') {
          const curTime = t1.time() - startTime;
          let percent;
          if (curTime >= 0) {
            currentTime.current = secondsToMinute(curTime);
            percent = (curTime / duration) * 100;
            setPercentage(percent);
          }
          if (percent >= 99.9 && !isRestartingAtEndRef.current) {
            isRestartingAtEndRef.current = true;
            clearTimeout(replayTimerRef.current);
            replayTimerRef.current = setTimeout(() => {
              restartFromBeginning("percent-end");
            }, 300);
          }
        }
      }, 1000 / 30);
    }
    return () => {
      clearInterval(playSeekTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlayAll, isPausePlayer, startTime, t1, duration, animoPlayerId, isAireframeShort, previewStartTime, restartFromBeginning]);

  useEffect(() => {
    if (
      !pausePlayer ||
      !isPlayAll ||
      !t1 ||
      typeof t1.time !== "function" ||
      isRestartingAtEndRef.current ||
      startTime === undefined ||
      startTime === null
    ) {
      return;
    }

    const timelineTime = t1.time();
    const timelineDuration = typeof t1.duration === "function" ? t1.duration() : duration;
    const isPausedAtEnd = timelineDuration > 0 && timelineTime >= timelineDuration - 0.05;

    if (!isPausedAtEnd) {
      return;
    }

    isRestartingAtEndRef.current = true;
    clearTimeout(replayTimerRef.current);
    replayTimerRef.current = setTimeout(() => {
      restartFromBeginning("pause-at-end");
    }, 300);
  }, [pausePlayer, isPlayAll, t1, duration, startTime, animoPlayerId, isAireframeShort, restartFromBeginning]);

  useEffect(() => {
    if (isPausePlayer) {
      clearInterval(playSeekTimer.current);
    }
  }, [isPausePlayer]);

  useEffect(() => {
    const handleFullScreenChange = () => {
      setIsFullScreen(!!document.fullscreenElement);
      dispatch({
        type: ActionTypes.SET_RESIZE,
      });
    };
    document.addEventListener("fullscreenchange", handleFullScreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullScreenChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePlayPause = () => {
    // Block all playback interaction while AI Reframe is being applied.
    if (isReframeApplying) return;
    // Don't allow playback if startTime is not available
    if (startTime === undefined || startTime === null) {
      return;
    }

    // Show play/pause icon for 2 seconds
    if (isShorts) {
      setShowPlayPauseIcon(true);
      if (playPauseIconTimer.current) {
        clearTimeout(playPauseIconTimer.current);
      }
      playPauseIconTimer.current = setTimeout(() => {
        setShowPlayPauseIcon(false);
      }, 2000);
    }

    if (videoRef?.current && !videoRef.current.paused) {
      videoRef.current.pause();
      videoRef.current.currentTime = previewStartTime;
      videoRef.current.style.display = "none";
      setShortsContext(SET_ACTIVE_PLAYER, null);
    }

    // Safely get playhead with fallback to startTime if t1 is not available
    let playhead = startTime;
    if (t1 && typeof t1.time === 'function') {
      playhead = t1.time();
    }

    if (playhead === endTime) {
      dispatch(playPausePlayer({ playhead: startTime, isPause: false }));
      setShortsContext(SET_ACTIVE_PLAYER, animoPlayerId);
      dispatch(showFeedbackModal({ show: false, view: false }));
      isManuallyPausedRef.current = false;
      return;
    }

    if (!isPlayAll && isPausePlayer) {
      // Set global playhead to startTime before starting playback
      dispatch(setPlayHeadTime({ playhead: startTime }));
      dispatch(playPausePlayer({ playhead: startTime, isPause: false }));
      dispatch(setPlayAll(true, false));
      setShortsContext(SET_ACTIVE_PLAYER, animoPlayerId);
      isManuallyPausedRef.current = false;
    } else if (!isPlayAll) {
      // Initial play - ensure timeline starts from startTime
      dispatch(setPlayHeadTime({ playhead: startTime }));
      dispatch(setPlayAll(true, false));
      setShortsContext(SET_ACTIVE_PLAYER, animoPlayerId);
      isManuallyPausedRef.current = false;
    } else if (isPausePlayer && isPlayAll) {
      // Play after pause
      dispatch(playPausePlayer({ playhead, isPause: false }));
      setShortsContext(SET_ACTIVE_PLAYER, animoPlayerId);
      dispatch(showFeedbackModal({ show: false, view: false }));
      isManuallyPausedRef.current = false;
    } else if (!isPausePlayer && isPlayAll) {
      // Pause
      dispatch(playPausePlayer({ playhead, isPause: true }));
      setShortsContext(SET_ACTIVE_PLAYER, null);
      dispatch(showFeedbackModal({ show: true, view: false }));
      isManuallyPausedRef.current = true;
    }
  };

  const seekHandler = (e) => {
    const seekBound = seekBarRef.current?.getBoundingClientRect();
    const seekPercentage = (e.pageX - seekBound.x) / seekBound.width;
    const playhead = seekPercentage * duration;
    setPercentage((playhead / duration) * 100);
    currentTime.current = secondsToMinute(playhead);
    dispatch(setPlayHeadTime({ playhead: playhead + startTime }));
  };

  const handleFullScreen = (fullScreen) => {
    setIsFullScreen(fullScreen);
    dispatch(setAnimoFullScreen({ isFullScreen: fullScreen }));
  };

  const fullScreenHandler = () => {
    let playhead = startTime;
    if (t1 && typeof t1.time === 'function') {
      playhead = t1.time();
    }
    dispatch(playPausePlayer({ playhead, isPause: true }));
    if (!document.fullscreenElement) {
      document.getElementById(animoPlayerId).requestFullscreen();
      handleFullScreen(true);
    } else {
      document.exitFullscreen();
      handleFullScreen(false);
    }
  };

  useEffect(() => {
    if (isPlayPause) {
      let playhead = startTime;
      if (t1 && typeof t1.time === 'function') {
        playhead = t1.time();
      }
      dispatch(playPausePlayer({ playhead, isPause: true }));
      setShortsContext(SET_STYLES_OPEN, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlayPause, t1, startTime]);

  useEffect(() => {
    const wasPresetOpen = prevPresetOpenRef.current;
    prevPresetOpenRef.current = presetModalOpen;

    if (!wasPresetOpen || presetModalOpen || !isShorts) {
      return;
    }

    if (isManuallyPausedRef.current) {
      return;
    }

    let playhead = startTime;
    if (t1 && typeof t1.time === "function") {
      playhead = t1.time();
    }

    dispatch(playPausePlayer({ playhead, isPause: false }));
    dispatch(setPlayAll(true, false));
    setShortsContext(SET_ACTIVE_PLAYER, animoPlayerId);

    // Resume through the composed animo player, which renders the freshly
    // applied subtitle style. Do NOT display the raw <Preview> video here: it
    // is absolutely positioned over the composed workspace and has no subtitle,
    // so showing it would hide the just-updated subtitle in the player.
    if (videoRef?.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = previewStartTime || 0;
      videoRef.current.style.display = "none";
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presetModalOpen, isShorts, startTime, t1, animoPlayerId]);

  useEffect(() => {
    if (percentage >= 99) {
      dispatch(showFeedbackModal({ show: true, view: false }));
    }
  }, [percentage, dispatch]);

  const changeVolumeHandler = (e) => {
    setVolume(e.target.value);
    dispatch(setGlobalVolume({ globalVolume: Number(e.target.value) }));
    let playhead = startTime;
    if (t1 && typeof t1.time === 'function') {
      playhead = t1.time();
    }
    dispatch(setPlayHeadTime({ playhead }));
  };

  const muteHandler = () => {
    let globalVolume;
    if (volume) {
      setVolume(0);
      globalVolume = 0;
    } else {
      setVolume(1);
      globalVolume = 1;
    }
    dispatch(setGlobalVolume({ globalVolume }));
    // Seek the player to update the current volume.
    let playhead = startTime;
    if (t1 && typeof t1.time === 'function') {
      playhead = t1.time();
    }
    dispatch(setPlayHeadTime({ playhead }));
  };

  const mouseEnterHandler = () => {
    setIsShowControl(true);
    // Only show the background preview video once the GSAP player has fully loaded
    // AND the video element has loaded metadata and seeked to the correct start time.
    // Prevents the raw video from playing behind the spinner/buffering loader and
    // avoids starting from the beginning of the full source file.
    if (!isPlayAll && isPlayerLoaded && isVideoReady && videoRef?.current && !isAireframeShort) {
      videoRef.current.currentTime = previewStartTime || 0;
      videoRef.current.style.display = "block";
      videoRef.current.play().catch(() => { });
    }
  };

  const mouseLeaveHandler = () => {
    setIsShowControl(false);
    if (videoRef?.current && !isPlayAll) {
      videoRef.current.pause();
      videoRef.current.currentTime = previewStartTime || 0;
      videoRef.current.style.display = "none";
    }
  };

  const volumeIcon =
    volume === 0 ? `mute` : volume <= 0.49 ? `low-volume` : `high-volume`;

  return (
    <>
      <PlayerControls
        onClick={handlePlayPause}
        onMouseEnter={mouseEnterHandler}
        onMouseLeave={mouseLeaveHandler}
        style={isReframeApplying ? { pointerEvents: "none" } : undefined}
      >


        <>
          {/* Initial play icon */}
          {!isPlayAll && (
            <PlayButton onClick={handlePlayPause} $disabled={isReframeApplying}>
              <Icons
                src={`${STATIC_PATH}shorts-play.svg`}
                alt="play"
                width="64px"
                height="64px"
                mobileWidth="48px"
                mobileHeight="48px"
              />
            </PlayButton>
          )}

          {/* Pause icon only after player loaded */}
          {isPlayAll &&
            isPlayerLoaded &&
            showPlayPauseIcon && (
              <PlayButton onClick={handlePlayPause} $disabled={isReframeApplying}>
                <Icons
                  src={`${STATIC_PATH}${!isPausePlayer
                    ? "shorts-pause"
                    : "shorts-play"
                    }.svg`}
                  alt={
                    !isPausePlayer
                      ? "pause"
                      : "play"
                  }
                  width="64px"
                  height="64px"
                  mobileWidth="48px"
                  mobileHeight="48px"
                />
              </PlayButton>
            )}

          {isShorts && (<Duration
            right="auto"
            left="8px"
            style={{
              width: "fit-content",
              padding: "0px 6px",
            }}
          >
            {currentTime.current || secondsToMinute(0)} /{" "}
            {secondsToMinute(duration)}
          </Duration>)}
        </>
      </PlayerControls>
      {isPlayAll && (
        <PlayerContainer
          height={isShorts ? "6px" : "32px"}
          width={isShorts ? "100%" : "98%"}
          gap={'8px'}
          display={isShowControl ? "flex" : "none"}
          isShorts={isShorts}
          onMouseEnter={() => setIsShowControl(true)}
        >
          {!isShorts && <Icons
            height="22px"
            width="22px"
            src={`${STATIC_PATH}${isPlayAll && !isPausePlayer ? "animo-pause" : "play-icon"}.svg`}
            id="playIcon"
            alt=""
            onClick={handlePlayPause}
          />}
          {!isShorts && <PlayTimer>
            {currentTime.current || secondsToMinute(duration)}
          </PlayTimer>}
          <SeekBar
            ref={seekBarRef}
            onClick={seekHandler}
            isFullScreen={isFullScreen}
          >
            <Seek width={`${percentage}%`} />
          </SeekBar>
          {!isShorts && <VolumeContainer>
            <Icons
              height="22px"
              width="22px"
              src={`${STATIC_PATH}animo-${volumeIcon}.svg`}
              alt="animo-volume"
              onClick={muteHandler}
            />
            <VolumeSlider className="animo-volume-tool-bar">
              <Slider
                width={"64px"}
                height={"4px"}
                border={"1px solid #DADADC"}
                borderRadius={"2px"}
                background={"#DADADC"}
                margin={"0"}
                thumb={{
                  background: "#637191 0% 0% no-repeat padding-box",
                  border: "none",
                  height: "10px",
                  width: "10px",
                  hoverBG: "#00A7E5",
                  hoverBorder: "1.7px solid #637191",
                }}
                onChangeSliderValue={() => { }}
                onMouseUp={changeVolumeHandler}
                value={volume}
                min={0}
                max={1}
                inputMin={0}
                inputMax={1}
                step={0.01}
                progressBackground={`#00A7E5`}
                isChangeBg={true}
              />
            </VolumeSlider>
          </VolumeContainer>}
          {!isShorts && !isIosSafari && (
            <Icons
              height="22px"
              width="22px"
              src={`${STATIC_PATH}fullscreen${isFullScreen ? "-exit" : ""}.svg`}
              alt=""
              onClick={fullScreenHandler}
            />
          )}
        </PlayerContainer>
      )}
    </>
  );
};

ShortsPlayerControlPanel.propTypes = {
  startTime: PropTypes.number,
  endTime: PropTypes.number,
  videoStartTime: PropTypes.number,
  isAireframeShort: PropTypes.bool,
  playbackVersion: PropTypes.number,
  videoRef: PropTypes.object,
  isShorts: PropTypes.bool,
  isVideoReady: PropTypes.bool,
};

export default ShortsPlayerControlPanel;
